export const CHIAVE_TEMA = 'calcio-tema';
export const CHIAVE_MODO = 'calcio-modo';

export type Modo = 'chiaro' | 'scuro';

export type Categoria = 'app' | 'sci-fi';

export const CATEGORIE: readonly { id: Categoria; etichetta: string }[] = [
  { id: 'app', etichetta: 'App' },
  { id: 'sci-fi', etichetta: 'Sci-Fi' },
];

export const PALETTE = [
  { id: 'carta', etichetta: 'Carta', categoria: 'app', fonti: [] },
  {
    id: 'invincible',
    etichetta: 'Invincible',
    categoria: 'app',
    fonti: ['Inter', 'JetBrains Mono'],
  },
  {
    id: 'magic-2',
    etichetta: 'Magic 2',
    categoria: 'app',
    fonti: ['Roboto', 'Merriweather', 'Roboto Mono'],
  },
  { id: 'rewaff', etichetta: 'Rewaff', categoria: 'app', fonti: ['Space Grotesk', 'Space Mono'] },
  { id: 'jamaica', etichetta: 'Jamaica', categoria: 'app', fonti: ['Outfit', 'JetBrains Mono'] },
  {
    id: 'minimal-neutral',
    etichetta: 'Minimal Neutral',
    categoria: 'app',
    fonti: ['DM Sans', 'Geist Mono'],
  },
  { id: 'swiss-design', etichetta: 'Swiss Design', categoria: 'app', fonti: ['Inter'] },
  {
    id: 'mx-brutalist',
    etichetta: 'MX-Brutalist',
    categoria: 'app',
    fonti: ['Montserrat', 'Space Mono'],
  },
  { id: 'polaris', etichetta: 'Polaris', categoria: 'app', fonti: ['Google Sans Flex'] },
  {
    id: 'meridian',
    etichetta: 'Meridian',
    categoria: 'app',
    fonti: ['Geist', 'Geist Mono', 'Outfit', 'Fira Code'],
  },
  {
    id: 'dark-forge',
    etichetta: 'Dark Forge',
    categoria: 'app',
    fonti: ['IBM Plex Sans', 'IBM Plex Mono'],
  },
  {
    id: 'party-rock',
    etichetta: 'Party Rock',
    categoria: 'app',
    fonti: ['Inter', 'JetBrains Mono'],
  },
  {
    id: 'rose-pine',
    etichetta: 'Rosé Pine Hyperlegible',
    categoria: 'app',
    fonti: ['Atkinson Hyperlegible', 'JetBrains Mono'],
  },
  { id: 'vs-code', etichetta: 'VS Code', categoria: 'app', fonti: ['Inter'] },
  { id: 'brownie', etichetta: 'Brownie', categoria: 'app', fonti: ['DM Sans'] },
  { id: 'terminal', etichetta: 'Terminal', categoria: 'sci-fi', fonti: ['VT323'] },
  { id: 'amber-mono', etichetta: 'Amber Mono 2.0', categoria: 'sci-fi', fonti: ['Geist Mono'] },
  {
    id: 'terminal-russian',
    etichetta: 'Terminal Dark Russian',
    categoria: 'sci-fi',
    fonti: ['JetBrains Mono'],
  },
  {
    id: 'teenage-lobster',
    etichetta: 'Teenage Lobster',
    categoria: 'sci-fi',
    fonti: ['JetBrains Mono'],
  },
  { id: 'fallout', etichetta: 'Fallout', categoria: 'sci-fi', fonti: ['Roboto Condensed'] },
  { id: 'gruvbox', etichetta: 'Gruvbox', categoria: 'sci-fi', fonti: ['Fira Code'] },
  { id: '2077', etichetta: '2077', categoria: 'sci-fi', fonti: ['Chakra Petch', 'IBM Plex Mono'] },
  {
    id: 'cyberpunk',
    etichetta: 'Cyberpunk',
    categoria: 'sci-fi',
    fonti: ['Orbitron', 'Fira Code'],
  },
  { id: 'omegon', etichetta: 'Omegon', categoria: 'sci-fi', fonti: ['Inter', 'JetBrains Mono'] },
  {
    id: 'styrene',
    etichetta: 'Styrene',
    categoria: 'sci-fi',
    fonti: ['Tomorrow', 'JetBrains Mono'],
  },
  { id: 'cyantrix', etichetta: 'Cyantrix', categoria: 'sci-fi', fonti: ['Oxanium', 'Kode Mono'] },
  { id: 'space', etichetta: 'Space', categoria: 'sci-fi', fonti: ['Rajdhani', 'JetBrains Mono'] },
  {
    id: 'optimus',
    etichetta: 'Optimus',
    categoria: 'sci-fi',
    fonti: ['Orbitron', 'Share Tech Mono'],
  },
  { id: 'offworld', etichetta: 'Offworld', categoria: 'sci-fi', fonti: ['Geist', 'Geist Mono'] },
  { id: 'jarvis', etichetta: 'Jarvis', categoria: 'sci-fi', fonti: ['Inter', 'IBM Plex Mono'] },
  { id: 'old-school', etichetta: 'Old School', categoria: 'sci-fi', fonti: [] },
  {
    id: 'melancholik-mint',
    etichetta: 'Melancholik Mint',
    categoria: 'sci-fi',
    fonti: ['Fira Code', 'IBM Plex Mono', 'JetBrains Mono'],
  },
  {
    id: 'control',
    etichetta: "Remedy's Control",
    categoria: 'sci-fi',
    fonti: ['Montserrat', 'JetBrains Mono'],
  },
] as const satisfies readonly {
  id: string;
  etichetta: string;
  categoria: Categoria;
  fonti: readonly string[];
}[];

export type IdTema = (typeof PALETTE)[number]['id'];

const IDS = new Set<string>(PALETTE.map((p) => p.id));

export function normalizzaTema(valore: string | null | undefined): IdTema {
  return IDS.has(valore ?? '') ? (valore as IdTema) : 'carta';
}

export function normalizzaModo(valore: string | null | undefined): Modo {
  return valore === 'scuro' ? 'scuro' : 'chiaro';
}

export function fontiDi(tema: string | null | undefined): readonly string[] {
  const id = normalizzaTema(tema);
  return PALETTE.find((p) => p.id === id)?.fonti ?? [];
}

export function urlFonti(tema: string | null | undefined): string | null {
  const famiglie = fontiDi(tema);
  if (famiglie.length === 0) return null;
  const query = famiglie
    .map((f) => `family=${encodeURIComponent(f).replaceAll('%20', '+')}:wght@400;500;600;700`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}
